/**
 * Formula engine for the blog Calculator component.
 *
 * Marketing types a formula as plain text in a Builder field, referencing input
 * keys by name, e.g. "(successful / scheduled) * 100". The engine tokenises it,
 * converts to postfix with the shunting-yard algorithm and evaluates on a
 * stack. There is no eval() and no new dependency.
 *
 * Nothing here throws at a reader. Every failure path returns
 * { ok: false, error } with a message written for whoever is configuring the
 * calculator in Builder, so a bad formula is visible in the editor and renders
 * as a neutral empty state in the post.
 *
 * Every formula in the Integration Health series is scalar arithmetic. The
 * percentile and median metrics in that series are published as text rather
 * than as calculators, so values here are always single numbers.
 */

export type FormulaScope = Record<string, number>;

export type FormulaResult = { ok: true; value: number } | { ok: false; error: string };

type BinaryOperator = '+' | '-' | '*' | '/';

type Token =
  | { type: 'number'; value: number }
  | { type: 'name'; value: string }
  | { type: 'operator'; value: BinaryOperator }
  | { type: 'unaryMinus' }
  | { type: 'openParen' }
  | { type: 'closeParen' }
  | { type: 'comma' };

const BINARY_PRECEDENCE: Record<BinaryOperator, number> = {
  '+': 1,
  '-': 1,
  '*': 2,
  '/': 2,
};

// Unary minus binds tighter than any binary operator, so -a * b is (-a) * b.
const UNARY_PRECEDENCE = 3;

/**
 * Word and the source formula doc use typographic operators (×, ÷, − U+2212)
 * and non-breaking spaces. Marketing will paste formulas straight out of that
 * doc, so fold those onto their ASCII equivalents before tokenising rather than
 * rejecting them as unexpected characters.
 */
const normalizeFormula = (formula: string): string =>
  formula
    .replace(/[\u00d7\u2217]/g, '*')
    .replace(/[\u00f7\u2215]/g, '/')
    .replace(/[\u2212\u2012\u2013\u2014]/g, '-')
    .replace(/[\u00a0\u2007\u202f]/g, ' ');

const isDigit = (char: string): boolean => char >= '0' && char <= '9';

const isNameStart = (char: string): boolean =>
  (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '_';

const isNameChar = (char: string): boolean => isNameStart(char) || isDigit(char);

const tokenize = (formula: string): { ok: true; tokens: Token[] } | { ok: false; error: string } => {
  const source = normalizeFormula(formula);
  const tokens: Token[] = [];
  let index = 0;

  while (index < source.length) {
    const char = source[index];

    if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      index += 1;
      continue;
    }

    if (isDigit(char) || (char === '.' && isDigit(source[index + 1] ?? ''))) {
      let end = index;
      while (end < source.length && isDigit(source[end])) {
        end += 1;
      }
      if (source[end] === '.') {
        end += 1;
        while (end < source.length && isDigit(source[end])) {
          end += 1;
        }
      }
      const raw = source.slice(index, end);
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        return { ok: false, error: `"${raw}" is not a valid number.` };
      }
      tokens.push({ type: 'number', value });
      index = end;
      continue;
    }

    if (isNameStart(char)) {
      let end = index;
      while (end < source.length && isNameChar(source[end])) {
        end += 1;
      }
      tokens.push({ type: 'name', value: source.slice(index, end) });
      index = end;
      continue;
    }

    if (char === '+' || char === '-' || char === '*' || char === '/') {
      // A minus is unary when nothing that can end a value precedes it, so both
      // "-a" and "b * -a" parse, while "b - a" stays binary.
      const previous = tokens[tokens.length - 1];
      const followsValue =
        previous !== undefined &&
        (previous.type === 'number' || previous.type === 'name' || previous.type === 'closeParen');

      if (char === '-' && !followsValue) {
        tokens.push({ type: 'unaryMinus' });
      } else if (char === '+' && !followsValue) {
        // A leading plus is a no-op, drop it rather than failing on "+5".
        index += 1;
        continue;
      } else {
        tokens.push({ type: 'operator', value: char });
      }
      index += 1;
      continue;
    }

    if (char === '(') {
      tokens.push({ type: 'openParen' });
      index += 1;
      continue;
    }

    if (char === ')') {
      tokens.push({ type: 'closeParen' });
      index += 1;
      continue;
    }

    if (char === ',') {
      tokens.push({ type: 'comma' });
      index += 1;
      continue;
    }

    return { ok: false, error: `Unexpected character "${char}" in the formula.` };
  }

  return { ok: true, tokens };
};

/**
 * Thrown internally and caught at the public boundary. Keeps the evaluator
 * readable without threading a result type through every helper.
 */
class FormulaError extends Error {}

// Annotated on the const rather than the arrow, which is what lets TypeScript
// treat a fail() call as unreachable-after and narrow the value it guards.
const fail: (message: string) => never = message => {
  throw new FormulaError(message);
};

type FormulaFunction = {
  maxArgs: number;
  minArgs: number;
  apply: (args: number[]) => number;
};

const FUNCTIONS: Record<string, FormulaFunction> = {
  abs: {
    minArgs: 1,
    maxArgs: 1,
    apply: args => Math.abs(args[0]),
  },
  max: {
    minArgs: 1,
    maxArgs: Infinity,
    apply: args => Math.max(...args),
  },
  min: {
    minArgs: 1,
    maxArgs: Infinity,
    apply: args => Math.min(...args),
  },
  round: {
    minArgs: 1,
    maxArgs: 2,
    apply: args => {
      const [value, decimals = 0] = args;

      if (!Number.isInteger(decimals) || decimals < 0 || decimals > 15) {
        return fail('round() takes a whole number of decimal places between 0 and 15.');
      }

      const factor = 10 ** decimals;

      return Math.round(value * factor) / factor;
    },
  },
};

type RpnItem =
  | { type: 'number'; value: number }
  | { type: 'name'; value: string }
  | { type: 'operator'; value: BinaryOperator }
  | { type: 'unaryMinus' }
  | { type: 'call'; name: string; argCount: number };

type StackItem =
  | { kind: 'operator'; value: BinaryOperator }
  | { kind: 'unaryMinus' }
  | { kind: 'function'; name: string }
  | { kind: 'openParen' };

const precedenceOf = (item: StackItem): number => {
  if (item.kind === 'operator') {
    return BINARY_PRECEDENCE[item.value];
  }

  return item.kind === 'unaryMinus' ? UNARY_PRECEDENCE : 0;
};

const toRpnItem = (item: StackItem): RpnItem =>
  item.kind === 'operator' ? { type: 'operator', value: item.value } : { type: 'unaryMinus' };

const describeArity = (fn: FormulaFunction): string => {
  if (fn.maxArgs === Infinity) {
    return `at least ${fn.minArgs}`;
  }

  if (fn.minArgs === fn.maxArgs) {
    return `${fn.minArgs}`;
  }

  return `${fn.minArgs} to ${fn.maxArgs}`;
};

/**
 * Shunting-yard: infix tokens to postfix, tracking an argument count per open
 * function call so the variadic helpers know how many operands to pop.
 */
const toRpn = (tokens: Token[]): RpnItem[] => {
  const output: RpnItem[] = [];
  const stack: StackItem[] = [];
  const argCounts: number[] = [];
  const sawArgument: boolean[] = [];

  const markArgument = () => {
    if (sawArgument.length > 0) {
      sawArgument[sawArgument.length - 1] = true;
    }
  };

  const popUntilOpenParen = () => {
    while (stack.length > 0 && stack[stack.length - 1].kind !== 'openParen') {
      output.push(toRpnItem(stack.pop() as StackItem));
    }

    if (stack.length === 0) {
      fail('The formula has a closing bracket with no matching opening bracket.');
    }
  };

  tokens.forEach((token, index) => {
    switch (token.type) {
      case 'number':
        output.push({ type: 'number', value: token.value });
        markArgument();
        break;

      case 'name': {
        const isCall = tokens[index + 1]?.type === 'openParen';

        if (!isCall) {
          output.push({ type: 'name', value: token.value });
          markArgument();
          break;
        }

        if (!(token.value in FUNCTIONS)) {
          fail(`"${token.value}" is not a function the calculator knows.`);
        }

        stack.push({ kind: 'function', name: token.value });
        argCounts.push(0);
        sawArgument.push(false);
        break;
      }

      case 'operator': {
        const precedence = BINARY_PRECEDENCE[token.value];

        while (stack.length > 0 && precedenceOf(stack[stack.length - 1]) >= precedence) {
          output.push(toRpnItem(stack.pop() as StackItem));
        }

        stack.push({ kind: 'operator', value: token.value });
        break;
      }

      case 'unaryMinus':
        // Right-associative, so it never pops an equal-precedence unary minus.
        stack.push({ kind: 'unaryMinus' });
        break;

      case 'openParen':
        stack.push({ kind: 'openParen' });
        break;

      case 'closeParen': {
        popUntilOpenParen();
        stack.pop();

        const enclosing = stack[stack.length - 1];

        if (enclosing?.kind === 'function') {
          stack.pop();

          const counted = argCounts.pop() ?? 0;
          const argCount = counted + (sawArgument.pop() === true ? 1 : 0);
          const fn = FUNCTIONS[enclosing.name];

          if (argCount < fn.minArgs || argCount > fn.maxArgs) {
            fail(`${enclosing.name}() takes ${describeArity(fn)} arguments, but got ${argCount}.`);
          }

          output.push({ type: 'call', name: enclosing.name, argCount });
          markArgument();
        }

        break;
      }

      case 'comma': {
        // Checked before unwinding the stack so a stray comma reports itself
        // rather than surfacing as a bracket-matching error.
        if (argCounts.length === 0) {
          fail('A comma can only separate the arguments of a function.');
        }

        popUntilOpenParen();
        argCounts[argCounts.length - 1] += 1;
        break;
      }

      default:
        break;
    }
  });

  while (stack.length > 0) {
    const item = stack.pop() as StackItem;

    if (item.kind === 'openParen' || item.kind === 'function') {
      fail('The formula has an opening bracket that is never closed.');
    }

    output.push(toRpnItem(item));
  }

  return output;
};

const applyBinary = (operator: BinaryOperator, left: number, right: number): number => {
  switch (operator) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '*':
      return left * right;
    default:
      return right === 0 ? fail('The formula divides by zero.') : left / right;
  }
};

const evaluateRpn = (items: RpnItem[], scope: FormulaScope): number => {
  const stack: number[] = [];

  items.forEach(item => {
    switch (item.type) {
      case 'number':
        stack.push(item.value);
        break;

      case 'name': {
        const value = scope[item.value];

        if (value === undefined) {
          fail(`The formula refers to "${item.value}", which is not one of the inputs.`);
        }

        stack.push(value);
        break;
      }

      case 'operator': {
        const right = stack.pop();
        const left = stack.pop();

        if (left === undefined || right === undefined) {
          fail(`There is nothing for "${item.value}" to work on.`);
        }

        stack.push(applyBinary(item.value, left, right));
        break;
      }

      case 'unaryMinus': {
        const value = stack.pop();

        if (value === undefined) {
          fail('There is nothing for the minus sign to negate.');
        }

        stack.push(-value);
        break;
      }

      case 'call': {
        const args = stack.splice(stack.length - item.argCount, item.argCount);

        if (args.length !== item.argCount) {
          fail(`${item.name}() did not get all of its arguments.`);
        }

        stack.push(FUNCTIONS[item.name].apply(args));
        break;
      }

      default:
        break;
    }
  });

  if (stack.length !== 1) {
    fail('The formula is incomplete.');
  }

  return stack[0];
};

/**
 * Evaluate a Builder-authored formula against the reader's current input values.
 *
 * @param formula - Formula text, e.g. "(successful / scheduled) * 100"
 * @param scope - Input keys mapped to the reader's current values
 * @returns The numeric result, or a message explaining what is wrong with it
 */
export const evaluateFormula = (formula: string, scope: FormulaScope): FormulaResult => {
  if (formula.trim() === '') {
    return { ok: false, error: 'This calculator has no formula yet.' };
  }

  const tokenized = tokenize(formula);

  if (!tokenized.ok) {
    return tokenized;
  }

  try {
    const value = evaluateRpn(toRpn(tokenized.tokens), scope);

    if (!Number.isFinite(value)) {
      return { ok: false, error: 'The formula does not produce a usable number.' };
    }

    return { ok: true, value };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof FormulaError ? error.message : 'The formula could not be calculated.',
    };
  }
};

/**
 * Collect the input keys a formula reads, so the editor can flag a formula that
 * references an input which no longer exists.
 *
 * @param formula - Formula text
 * @returns Referenced keys in first-seen order, excluding function names
 */
export const getFormulaReferences = (formula: string): string[] => {
  const tokenized = tokenize(formula);

  if (!tokenized.ok) {
    return [];
  }

  return tokenized.tokens.reduce<string[]>((all, token, index) => {
    const isCall = tokenized.tokens[index + 1]?.type === 'openParen';

    if (token.type !== 'name' || isCall || all.includes(token.value)) {
      return all;
    }

    return [...all, token.value];
  }, []);
};
